import React, { useState, useEffect, useMemo } from 'react';
import measurementService from '../../../../services/measurementService';
import patientService from '../../../../services/patientService';
import { checkBGValue } from '../../../../utils/measurementUtils';
import './PieChart.css';

const PieChart = ({ practiceId, patientId }) => {
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);

  useEffect(() => {
    if (practiceId && patientId) {
      fetchScheduleTargets();
      fetchMeasurements();
    }
  }, [practiceId, patientId]);

  const fetchScheduleTargets = async () => {
    try {
      const data = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
      setScheduleTargets(data);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    }
  };

  const fetchMeasurements = async () => {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const fromDateParam = startDate.toISOString().split('T')[0];
      const toDateParam = endDate.toISOString().split('T')[0];

      const response = await measurementService.getBloodGlucoseByDateRange(
        practiceId,
        patientId,
        fromDateParam,
        toDateParam,
        'daily_average'
      );

      if (response.data?.success) {
        setMeasurements(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);
      setMeasurements([]);
    }
  };

  // Get target for before/after meal
  const getTarget = (isBeforeMeal) => {
    if (!scheduleTargets?.bloodGlucoseTargetRange) {
      return isBeforeMeal ? { min: 60, max: 110 } : { min: 70, max: 140 };
    }
    const targets = scheduleTargets.bloodGlucoseTargetRange;
    const normalizeTargetKey = (value) =>
      String(value || '')
        .toLowerCase()
        .replace(/[\s_-]/g, '')
        .replace(/targetranges?$/, '')
        .replace(/targets?$/, '');
    
    if (isBeforeMeal) {
      const target = targets.find(t => 
        normalizeTargetKey(t.name).includes('beforemeal')
      ) || targets[0];
      const min = Number(target?.minimum ?? target?.min);
      const max = Number(target?.maximum ?? target?.max);
      return {
        min: Number.isNaN(min) ? 60 : min,
        max: Number.isNaN(max) ? 110 : max
      };
    }

    const target = targets.find(t => 
      normalizeTargetKey(t.name).includes('aftermeal')
    ) || targets[0];
    const min = Number(target?.minimum ?? target?.min);
    const max = Number(target?.maximum ?? target?.max);
    return {
      min: Number.isNaN(min) ? 70 : min,
      max: Number.isNaN(max) ? 140 : max
    };
  };

  // Calculate pie chart data
  const pieChartData = useMemo(() => {
    if (!measurements || measurements.length === 0) {
      return {
        beforeMeal: { above: 0, below: 0, within: 0, total: 0 },
        afterMeal: { above: 0, below: 0, within: 0, total: 0 }
      };
    }

    const beforeTarget = getTarget(true);
    const afterTarget = getTarget(false);

    const beforeMeal = { above: 0, below: 0, within: 0, total: 0 };
    const afterMeal = { above: 0, below: 0, within: 0, total: 0 };

    measurements.forEach(m => {
      const bgValue = parseFloat(m.blood_glucose_value_1 || m.blood_glucose_value);
      if (isNaN(bgValue)) return;

      const period = (m.period_name || m.period || m.measure_note || m.note || '').toLowerCase();
      const isBefore = period.includes('before');
      const isAfter = period.includes('after');

      if (isBefore) {
        beforeMeal.total++;
        const status = checkBGValue(bgValue, beforeTarget);
        if (status === 'high') beforeMeal.above++;
        else if (status === 'low') beforeMeal.below++;
        else beforeMeal.within++;
      } else if (isAfter) {
        afterMeal.total++;
        const status = checkBGValue(bgValue, afterTarget);
        if (status === 'high') afterMeal.above++;
        else if (status === 'low') afterMeal.below++;
        else afterMeal.within++;
      }
    });

    return { beforeMeal, afterMeal };
  }, [measurements, scheduleTargets]);

  // Calculate pie chart angles
  const calculatePieData = (data) => {
    if (data.total === 0) return null;

    const aboveAngle = (data.above / data.total) * 360;
    const belowAngle = (data.below / data.total) * 360;
    const withinAngle = (data.within / data.total) * 360;

    let currentAngle = 0;
    const segments = [];

    if (data.above > 0) {
      segments.push({
        angle: aboveAngle,
        startAngle: currentAngle,
        color: '#d32f2f', // Red
        label: 'Above Target',
        count: data.above
      });
      currentAngle += aboveAngle;
    }

    if (data.below > 0) {
      segments.push({
        angle: belowAngle,
        startAngle: currentAngle,
        color: '#03a9f4', // Light Blue
        label: 'Below Target',
        count: data.below
      });
      currentAngle += belowAngle;
    }

    if (data.within > 0) {
      segments.push({
        angle: withinAngle,
        startAngle: currentAngle,
        color: '#1976d2', // Dark Blue
        label: 'In-Range',
        count: data.within
      });
    }

    return segments;
  };

  const beforeMealPie = calculatePieData(pieChartData.beforeMeal);
  const afterMealPie = calculatePieData(pieChartData.afterMeal);

  // SVG path for pie segment
  const createPieSegment = (startAngle, angle, radius) => {
    const start = (startAngle - 90) * (Math.PI / 180);
    const end = (startAngle + angle - 90) * (Math.PI / 180);
    const x1 = radius + radius * Math.cos(start);
    const y1 = radius + radius * Math.sin(start);
    const x2 = radius + radius * Math.cos(end);
    const y2 = radius + radius * Math.sin(end);
    const largeArc = angle > 180 ? 1 : 0;

    return `M ${radius} ${radius} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  const radius = 80;

  return (
    <div className="bg-pie-chart-section">
      <h4>Pie Chart Of Blood Glucose</h4>
      <div className="bgpc-charts-container">
        {/* Before Meal Pie Chart */}
        <div className="bgpc-chart-wrapper">
          <div className="bgpc-chart-title">Before Meal</div>
          {pieChartData.beforeMeal.total === 0 ? (
            <div className="bgpc-no-data">No data for before meal</div>
          ) : (
            <div className="bgpc-chart-content">
              <svg width={radius * 2} height={radius * 2} viewBox={`0 0 ${radius * 2} ${radius * 2}`}>
                {beforeMealPie?.map((segment, index) => (
                  <path
                    key={index}
                    d={createPieSegment(segment.startAngle, segment.angle, radius)}
                    fill={segment.color}
                    stroke="white"
                    strokeWidth="2"
                  />
                ))}
              </svg>
              <div className="bgpc-legend">
                {beforeMealPie?.map((segment, index) => (
                  <div key={index} className="bgpc-legend-item">
                    <div className="bgpc-legend-color" style={{ backgroundColor: segment.color }}></div>
                    <span>{segment.label}: {segment.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* After Meal Pie Chart */}
        <div className="bgpc-chart-wrapper">
          <div className="bgpc-chart-title">After Meal</div>
          {pieChartData.afterMeal.total === 0 ? (
            <div className="bgpc-no-data">No data for after meal</div>
          ) : (
            <div className="bgpc-chart-content">
              <svg width={radius * 2} height={radius * 2} viewBox={`0 0 ${radius * 2} ${radius * 2}`}>
                {afterMealPie?.map((segment, index) => (
                  <path
                    key={index}
                    d={createPieSegment(segment.startAngle, segment.angle, radius)}
                    fill={segment.color}
                    stroke="white"
                    strokeWidth="2"
                  />
                ))}
              </svg>
              <div className="bgpc-legend">
                {afterMealPie?.map((segment, index) => (
                  <div key={index} className="bgpc-legend-item">
                    <div className="bgpc-legend-color" style={{ backgroundColor: segment.color }}></div>
                    <span>{segment.label}: {segment.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PieChart;

